/**
 * Custom Tabs — Section-scoped vanilla JavaScript
 * Supports ARIA, keyboard navigation, multiple instances, and Theme Editor events.
 */
(function () {
  class CustomTabs {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.tablist = sectionEl.querySelector('[role="tablist"]');
      this.tabs = [];
      this.panels = [];
      this._boundHandleKeydown = this.handleKeydown.bind(this);
      this._boundHandleClick = this.handleClick.bind(this);

      this.init();
    }

    /** Gather DOM references and bind events */
    init() {
      if (!this.tablist) return;

      this.tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => {
        const panelId = tab.getAttribute('aria-controls');
        return this.section.querySelector(`[data-panel-id="${panelId}"]`);
      });

      /* Bind events to the tablist for delegation */
      this.tablist.addEventListener('click', this._boundHandleClick);
      this.tablist.addEventListener('keydown', this._boundHandleKeydown);
    }

    /** Handle tab click */
    handleClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;

      const index = this.tabs.indexOf(tab);
      if (index !== -1) {
        this.activateTab(index);
      }
    }

    /**
     * Keyboard navigation
     * ArrowRight / ArrowLeft — move focus
     * Home — first tab
     * End — last tab
     */
    handleKeydown(event) {
      const { key } = event;
      const currentIndex = this.tabs.indexOf(document.activeElement);

      if (currentIndex === -1) return;

      let newIndex;

      switch (key) {
        case 'ArrowRight':
          newIndex = (currentIndex + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
          newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(newIndex);
      this.tabs[newIndex].focus();
    }

    /** Activate a tab by index */
    activateTab(index) {
      this.tabs.forEach((tab, i) => {
        const isSelected = i === index;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');

        const panel = this.panels[i];
        if (panel) {
          panel.setAttribute('aria-hidden', String(!isSelected));
          panel.classList.toggle('custom-tabs__content--active', isSelected);
        }
      });
    }

    /** Clean up event listeners */
    destroy() {
      if (!this.tablist) return;
      this.tablist.removeEventListener('click', this._boundHandleClick);
      this.tablist.removeEventListener('keydown', this._boundHandleKeydown);
    }
  }

  /* ---- Initialization ---- */

  /** Store active instances keyed by section ID */
  const instances = new Map();

  /** Initialize all custom-tabs sections on the page */
  function initAll() {
    document.querySelectorAll('[data-custom-tabs]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (!instances.has(id)) {
        instances.set(id, new CustomTabs(el));
      }
    });
  }

  /** Initialize a single section by ID (Theme Editor) */
  function initSection(sectionId) {
    destroySection(sectionId);
    const el = document.querySelector(
      `[data-custom-tabs][data-section-id="${sectionId}"]`
    );
    if (el) {
      instances.set(sectionId, new CustomTabs(el));
    }
  }

  /** Destroy a single section instance */
  function destroySection(sectionId) {
    if (instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }
  }

  /* ---- Theme Editor Events ---- */
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initSection(event.detail.sectionId);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroySection(event.detail.sectionId);
    });

    document.addEventListener('shopify:section:reorder', (event) => {
      initSection(event.detail.sectionId);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const sectionEl = event.target.closest('[data-custom-tabs]');
      if (!sectionEl) return;

      const sectionId = sectionEl.dataset.sectionId;
      initSection(sectionId);

      /* Activate the selected block's tab */
      const instance = instances.get(sectionId);
      if (instance) {
        const blockId = event.detail.blockId;
        const tabIndex = instance.tabs.findIndex((tab) => {
          const controls = tab.getAttribute('aria-controls');
          const panel = sectionEl.querySelector(
            `[data-panel-id="${controls}"]`
          );
          return (
            panel &&
            panel
              .closest('[data-shopify-editor-block]')
              ?.dataset.shopifyEditorBlock?.includes(blockId)
          );
        });
        if (tabIndex !== -1) {
          instance.activateTab(tabIndex);
        }
      }
    });
  }

  /* ---- Launch ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
